import React, { useState } from "react";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import { BarChart3, Calendar, Filter, GraduationCap, ClipboardList, Shield, FileText, TrendingUp, Search, UserSearch } from "lucide-react";

import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../../../components/ui/card";
import { Button } from "../../../components/ui/button";
import { Label } from "../../../components/ui/label";
import { DateHourPicker } from "../../../components/DateHourPicker";
import { Loading } from "../../../components/Loading";
import { OficiaisSelectField } from "../../../components/OficiaisSelectField";
import { desempenhoService } from "../../../services/desempenho";
import type {
  FiltroDesempenho,
  TreinamentosDesempenho,
  FilasDesempenho,
  PatrulhasDesempenho,
  RMDesempenho,
  RMODesempenho,
  OficialDesempenho,
} from "../../../types/desempenho";

import {
  TreinamentosTab,
  FilasTab,
  PatrulhasTab,
  RMTab,
  RMOTab,
  OficiaisTab,
} from "./components";

type TabType = "treinamentos" | "filas" | "patrulhas" | "rm" | "rmo" | "oficiais";

export default function Desempenho() {
  const [activeTab, setActiveTab] = useState<TabType>("treinamentos");
  const [loading, setLoading] = useState(false);
  const [loadingOficial, setLoadingOficial] = useState(false);

  // Filtros
  const [dataInicio, setDataInicio] = useState<Date>(
    new Date(new Date().setDate(new Date().getDate() - 7))
  );
  const [dataFim, setDataFim] = useState<Date>(new Date());
  const [granularidade, setGranularidade] = useState<"diaria" | "semanal" | "mensal">("diaria");

  // Filtro da aba Oficiais
  const [oficialId, setOficialId] = useState<string>("");
  const [oficialNick, setOficialNick] = useState<string>("");

  // Dados
  const [treinamentosData, setTreinamentosData] = useState<TreinamentosDesempenho | null>(null);
  const [filasData, setFilasData] = useState<FilasDesempenho | null>(null);
  const [patrulhasData, setPatrulhasData] = useState<PatrulhasDesempenho | null>(null);
  const [rmData, setRMData] = useState<RMDesempenho | null>(null);
  const [rmoData, setRMOData] = useState<RMODesempenho | null>(null);
  const [oficialData, setOficialData] = useState<OficialDesempenho | null>(null);

  const handleBuscar = async () => {
    setLoading(true);
    try {
      const filtro: FiltroDesempenho = {
        dataInicio: format(dataInicio, "yyyy-MM-dd'T'HH:mm"),
        dataFim: format(dataFim, "yyyy-MM-dd'T'HH:mm"),
        granularidade,
      };

      // Buscar dados de todas as tabs (exceto Oficiais)
      const [treinamentos, filas, patrulhas, rm, rmo] = await Promise.all([
        desempenhoService.getTreinamentosDesempenho(filtro),
        desempenhoService.getFilasDesempenho(filtro),
        desempenhoService.getPatrulhasDesempenho(filtro),
        desempenhoService.getRMDesempenho(filtro),
        desempenhoService.getRMODesempenho(filtro),
      ]);

      setTreinamentosData(treinamentos);
      setFilasData(filas);
      setPatrulhasData(patrulhas);
      setRMData(rm);
      setRMOData(rmo);

      // Se a aba Oficiais está ativa e há um oficial selecionado, rebusca também
      if (activeTab === "oficiais" && oficialId) {
        await buscarOficial(oficialId, filtro);
      }
    } catch (error) {
      console.error("Erro ao buscar dados de desempenho:", error);
      alert("Erro ao buscar dados de desempenho");
    } finally {
      setLoading(false);
    }
  };

  const buscarOficial = async (militarId: string, filtroBase?: FiltroDesempenho) => {
    const filtro = filtroBase ?? {
      dataInicio: format(dataInicio, "yyyy-MM-dd'T'HH:mm"),
      dataFim: format(dataFim, "yyyy-MM-dd'T'HH:mm"),
      granularidade,
    };
    setLoadingOficial(true);
    try {
      const data = await desempenhoService.getOficialDesempenho({ ...filtro, militarId });
      setOficialData(data);
    } catch (error) {
      console.error("Erro ao buscar dados do oficial:", error);
      alert("Erro ao buscar dados do oficial");
    } finally {
      setLoadingOficial(false);
    }
  };

  const handleOficialChange = (militarId: string, militarNick: string) => {
    setOficialId(militarId);
    setOficialNick(militarNick);
    setOficialData(null);
  };

  const handleBuscarOficial = () => {
    if (!oficialId) {
      alert("Selecione um oficial antes de buscar.");
      return;
    }
    buscarOficial(oficialId);
  };

  const tabs = [
    { id: "treinamentos", label: "Treinamentos", icon: GraduationCap },
    { id: "filas",        label: "Filas",        icon: ClipboardList },
    { id: "patrulhas",   label: "Patrulhas",     icon: Shield },
    { id: "rm",          label: "RM",            icon: FileText },
    { id: "rmo",         label: "RMO",           icon: TrendingUp },
    { id: "oficiais",    label: "Oficiais",       icon: UserSearch },
  ];

  return (
    <div className="space-y-6">
      <div className="border-b border-border pb-4">
        <h1 className="text-2xl font-bold text-foreground flex items-center gap-2">
          <BarChart3 className="h-6 w-6 text-[oklch(0.45_0.15_142)]" />
          Análise de Desempenho
        </h1>
        <p className="text-muted-foreground mt-2">
          Visualize métricas e estatísticas de desempenho operacional
        </p>
      </div>

      {/* Filtros */}
      <Card className="border-l-4 border-l-[oklch(0.45_0.15_142)]">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-[oklch(0.35_0.15_142)]">
                <Search className="w-5 h-5" />
                Buscar Desempenho
              </CardTitle>
              <CardDescription>
                Selecione o período e granularidade para análise
              </CardDescription>
            </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div>
              <Label>Data Início</Label>
              <DateHourPicker
                selected={dataInicio}
                onChange={(date) => date && setDataInicio(date)}
              />
            </div>

            <div>
              <Label>Data Fim</Label>
              <DateHourPicker
                selected={dataFim}
                onChange={(date) => date && setDataFim(date)}
              />
            </div>

            <div>
              <Label>Granularidade</Label>
              <select
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
                value={granularidade}
                onChange={(e) => setGranularidade(e.target.value as "diaria" | "semanal" | "mensal")}
              >
                <option value="diaria">Diária</option>
                <option value="semanal">Semanal</option>
                <option value="mensal">Mensal</option>
              </select>
            </div>

            <div className="flex items-end">
              <Button
                onClick={activeTab === "oficiais" ? handleBuscarOficial : handleBuscar}
                disabled={loading || loadingOficial}
                className="w-full bg-[oklch(0.45_0.15_142)] hover:bg-[oklch(0.40_0.15_142)] text-white"
              >
                {loading || loadingOficial ? "Buscando..." : "Buscar"}
              </Button>
            </div>
          </div>

          {/* Filtro de oficial — visível apenas na aba Oficiais */}
          {activeTab === "oficiais" && (
            <div className="mt-4 border-t border-border pt-4">
              <Label>Oficial</Label>
              <div className="max-w-xs mt-1">
                <OficiaisSelectField
                  value={oficialId}
                  onChange={handleOficialChange}
                  placeholder="Digite o nome do oficial..."
                />
              </div>
              {oficialId && (
                <p className="text-xs text-muted-foreground mt-1">
                  Selecionado: <span className="font-medium text-foreground">{oficialNick}</span>
                </p>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Tabs */}
      <div className="border-b border-border">
        <nav className="flex gap-2 flex-wrap" aria-label="Tabs">
          {tabs.map((tab) => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id as TabType)}
                className={`${
                  activeTab === tab.id
                    ? "border-[oklch(0.45_0.15_142)] text-[oklch(0.45_0.15_142)]"
                    : "border-transparent text-muted-foreground hover:text-foreground"
                } inline-flex items-center px-4 py-2 border-b-2 font-medium text-sm whitespace-nowrap transition-colors`}
              >
                <Icon
                  className={`${
                    activeTab === tab.id
                      ? "text-[oklch(0.45_0.15_142)]"
                      : "text-muted-foreground"
                  } -ml-0.5 mr-2 h-5 w-5`}
                />
                {tab.label}
              </button>
            );
          })}
        </nav>
      </div>

      {/* Conteúdo das Tabs */}
      {(loading && activeTab !== "oficiais") ? (
        <div className="flex justify-center py-12">
          <Loading />
        </div>
      ) : (
        <>
          {activeTab === "treinamentos" && (
            <TreinamentosTab data={treinamentosData} />
          )}
          {activeTab === "filas"      && <FilasTab     data={filasData} />}
          {activeTab === "patrulhas"  && <PatrulhasTab data={patrulhasData} />}
          {activeTab === "rm"         && <RMTab        data={rmData} />}
          {activeTab === "rmo"        && <RMOTab       data={rmoData} onRefresh={handleBuscar} />}
          {activeTab === "oficiais"   && (
            loadingOficial
              ? <div className="flex justify-center py-12"><Loading /></div>
              : <OficiaisTab data={oficialData} />
          )}
        </>
      )}
    </div>
  );
}
